import React from "react";
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
import { db, functions } from "../firebase";
import { useAuth } from "./AuthContext";
import ReviewStars from "./ReviewStars";
import { goToApp } from "../utils/crossDomainNav";
import { therapistReviewPath } from "../utils/reviewLink";
import { formatReviewCountLabel } from "../utils/reviewLabel";
import { isMarketingHost, shouldEnforceDomainSplit } from "../constants/siteDomains";
import { hasSessionMarker } from "../utils/sessionMarker";

type ReviewDoc = {
  id: string;
  authorName?: string;
  rating?: number;
  comment?: string;
  createdAt?: { toDate?: () => Date };
  appointmentId?: string;
};

function formatReviewDate(value?: { toDate?: () => Date }): string {
  const date = value?.toDate?.();
  if (!date) return "";
  return date.toLocaleDateString("pt-BR");
}

function callableErrorMessage(err: unknown): string {
  const error = err as { code?: string; message?: string; details?: string };
  const raw = String(error?.details || error?.message || "");
  if (error?.code === "functions/already-exists" || raw.toLowerCase().includes("já avaliou")) {
    return "Você já avaliou este constelador.";
  }
  if (error?.code === "functions/resource-exhausted") {
    return "Muitas avaliações neste momento. Tente novamente em alguns minutos.";
  }
  if (!raw || /^internal$/i.test(raw.trim()) || error?.code === "functions/internal") {
    return "Não foi possível enviar a avaliação agora. Tente novamente.";
  }
  return raw;
}

export default function TherapistReviews({
  therapistId,
  therapistName,
  reviewAverage = 0,
  reviewCount = 0,
  invite = false,
}: {
  therapistId: string;
  therapistName?: string;
  reviewAverage?: number;
  reviewCount?: number;
  invite?: boolean;
}) {
  const { user, isAuthenticated, isProfileComplete, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = React.useState<ReviewDoc[]>([]);
  const [alreadyReviewed, setAlreadyReviewed] = React.useState(false);
  const [appointmentId, setAppointmentId] = React.useState("");
  const [rating, setRating] = React.useState(5);
  const [comment, setComment] = React.useState("");
  const [guestName, setGuestName] = React.useState("");
  const [guestEmail, setGuestEmail] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const loggedIn = Boolean(isAuthenticated && isProfileComplete && user?.uid);
  const isOwnProfile = Boolean(user?.uid && user.uid === therapistId);
  const needsAccountOnApp = !authLoading && !loggedIn && shouldEnforceDomainSplit();

  const goToReviewOnApp = () => {
    const reviewPath = therapistReviewPath(therapistId);
    const loginPath = `/login?redirect=${encodeURIComponent(reviewPath)}`;
    if (isMarketingHost()) {
      goToApp(hasSessionMarker() ? reviewPath : loginPath, navigate);
      return;
    }
    navigate(loginPath);
  };

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const reviewsQuery = query(
        collection(db, "therapistReviews"),
        where("therapistId", "==", therapistId),
        orderBy("createdAt", "desc"),
        limit(20),
      );
      const snapshot = await getDocs(reviewsQuery);
      if (cancelled) return;
      setReviews(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as Omit<ReviewDoc, "id">) })));
    };
    load().catch((err) => {
      console.error("Erro ao carregar avaliações:", err);
    });
    return () => {
      cancelled = true;
    };
  }, [therapistId]);

  React.useEffect(() => {
    if (!loggedIn || !user?.uid) {
      setAlreadyReviewed(false);
      setAppointmentId("");
      return;
    }
    let cancelled = false;
    const loadEligible = async () => {
      const ownReview = await getDoc(doc(db, "therapistReviews", `${therapistId}_${user.uid}`));
      if (cancelled) return;
      if (ownReview.exists()) {
        setAlreadyReviewed(true);
        return;
      }
      setAlreadyReviewed(false);

      const appointmentsQuery = query(
        collection(db, "appointments"),
        where("pacienteId", "==", user.uid),
        limit(50),
      );
      const snapshot = await getDocs(appointmentsQuery);
      const now = Date.now();
      const next = snapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as any) }))
        .filter((item) => {
          if (item.terapeutaId !== therapistId) return false;
          if (item.status === "cancelado") return false;
          const session = item.dataSessao?.toDate?.() || (item.dataSessao ? new Date(item.dataSessao) : null);
          return session instanceof Date && session.getTime() <= now;
        })
        .sort((a, b) => {
          const aTime = a.dataSessao?.toDate?.()?.getTime?.() || 0;
          const bTime = b.dataSessao?.toDate?.()?.getTime?.() || 0;
          return bTime - aTime;
        });
      if (!cancelled && next[0]?.id) setAppointmentId(next[0].id);
    };
    loadEligible().catch((err) => {
      console.error("Erro ao buscar sessões para avaliar:", err);
    });
    return () => {
      cancelled = true;
    };
  }, [loggedIn, user?.uid, therapistId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (needsAccountOnApp) {
      goToReviewOnApp();
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const createReview = httpsCallable(functions, "createTherapistReview");
      await createReview({
        therapistId,
        rating,
        comment,
        ...(appointmentId ? { appointmentId } : {}),
        ...(!loggedIn ? { authorName: guestName.trim(), authorEmail: guestEmail.trim() } : {}),
      });
      setSuccess("Avaliação publicada. Obrigado!");
      setComment("");
      setAlreadyReviewed(true);
      setReviews((prev) => [
        {
          id: `${therapistId}_${user?.uid || "guest"}`,
          authorName: loggedIn ? user?.name || "Você" : guestName.trim(),
          rating,
          comment,
          createdAt: { toDate: () => new Date() },
          appointmentId,
        },
        ...prev,
      ]);
    } catch (err: unknown) {
      setError(callableErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const showForm = !isOwnProfile && !alreadyReviewed && !success && !needsAccountOnApp;
  const showLoginCta = !isOwnProfile && !alreadyReviewed && !success && needsAccountOnApp;

  const loginCta = showLoginCta ? (
    <div className={invite ? "mb-8 space-y-3" : "mb-8 rounded-xl border border-[#D5E1DB] bg-[#F7FBF9] p-4"}>
      {!invite && (
        <>
          <p className="mb-2 text-sm font-medium text-[#2E4B4C]">
            Avalie {therapistName ? therapistName : "este constelador"}
          </p>
          <p className="mb-3 text-xs text-[#5A7272]">
            Uma avaliação por pessoa. Pode avaliar mesmo quem não agendou pela plataforma.
          </p>
        </>
      )}
      <p className="text-sm text-[#2E4B4C]">
        {isMarketingHost()
          ? "O login fica na área do aplicativo. Se você já entrou, continue por lá para a avaliação usar a sua conta."
          : "Entre na sua conta para publicar a avaliação."}
      </p>
      <button
        type="button"
        onClick={goToReviewOnApp}
        className="rounded-full bg-[#4A7C75] px-5 py-2 text-sm font-semibold text-white hover:bg-[#406b65]"
      >
        {isMarketingHost() && hasSessionMarker() ? "Avaliar com minha conta" : "Entrar para avaliar"}
      </button>
    </div>
  ) : null;

  const form = showForm ? (
    <form
      onSubmit={handleSubmit}
      className={invite ? "mb-8 space-y-4" : "mb-8 rounded-xl border border-[#D5E1DB] bg-[#F7FBF9] p-4"}
    >
      {!invite && (
        <>
          <p className="mb-3 text-sm font-medium text-[#2E4B4C]">
            Avalie {therapistName ? therapistName : "este constelador"}
          </p>
          <p className="mb-3 text-xs text-[#5A7272]">
            Uma avaliação por pessoa. Pode avaliar mesmo quem não agendou pela plataforma.
          </p>
        </>
      )}
      {!loggedIn && (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm text-[#2E4B4C]">
            Seu nome
            <input
              required
              minLength={2}
              maxLength={80}
              value={guestName}
              onChange={(event) => setGuestName(event.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-sm text-[#2E4B4C]">
            Seu e-mail
            <input
              required
              type="email"
              value={guestEmail}
              onChange={(event) => setGuestEmail(event.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <span className="mt-1 block text-xs text-[#85A7A8]">Não aparece na vitrine. Só evita avaliação duplicada.</span>
          </label>
        </div>
      )}
      <div>
        <ReviewStars value={rating} onChange={setRating} label="Sua nota" />
      </div>
      <textarea
        required
        minLength={8}
        maxLength={500}
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        placeholder="Como foi a sessão? Escreva um comentário sincero."
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        rows={4}
      />
      {error && <p className="text-sm text-red-700">{error}</p>}
      {success && <p className="text-sm text-green-700">{success}</p>}
      <button
        type="submit"
        disabled={saving}
        className="rounded-full bg-[#4A7C75] px-5 py-2 text-sm font-semibold text-white hover:bg-[#406b65] disabled:opacity-60"
      >
        {saving ? "Enviando..." : "Publicar avaliação"}
      </button>
    </form>
  ) : null;

  const list = alreadyReviewed && success ? (
    <p className="mb-6 text-sm text-green-700">{success}</p>
  ) : alreadyReviewed && !showForm ? (
    <p className="mb-6 text-sm text-[#5A7272]">Você já avaliou este constelador.</p>
  ) : null;

  if (invite) {
    return (
      <div>
        {loginCta}
        {form}
        {list}
        {reviews.length === 0 ? (
          <p className="text-sm text-[#5A7272]">As primeiras avaliações deste constelador aparecem aqui.</p>
        ) : (
          <ul className="space-y-4">
            {reviews.map((review) => (
              <li key={review.id} className="rounded-xl border border-[#E8EFEB] p-4">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <p className="font-semibold text-[#6C4E78]">{review.authorName || "Paciente"}</p>
                  <span className="text-xs text-[#85A7A8]">{formatReviewDate(review.createdAt)}</span>
                </div>
                <ReviewStars value={Number(review.rating) || 0} size="sm" />
                <p className="mt-2 whitespace-pre-line text-sm leading-6 text-[#4A7C75]">{review.comment}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <section className="mt-8 rounded-2xl bg-white p-6 shadow-md">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-semibold" style={{ color: "#6C4E78" }}>
          Avaliações
        </h2>
        <div className="flex items-center gap-2">
          <ReviewStars value={reviewAverage} />
          <span className="text-sm text-[#4A7C75]">
            {reviewCount > 0 ? `${reviewAverage.toFixed(1)} · ${formatReviewCountLabel(reviewCount)}` : "Ainda sem avaliações"}
          </span>
        </div>
      </div>

      {loginCta}
      {form}
      {list}

      {reviews.length === 0 ? (
        <p className="text-sm text-[#5A7272]">As primeiras avaliações deste constelador aparecem aqui.</p>
      ) : (
        <ul className="space-y-4">
          {reviews.map((review) => (
            <li key={review.id} className="rounded-xl border border-[#E8EFEB] p-4">
              <div className="mb-2 flex items-center justify-between gap-3">
                <p className="font-semibold text-[#6C4E78]">{review.authorName || "Paciente"}</p>
                <span className="text-xs text-[#85A7A8]">{formatReviewDate(review.createdAt)}</span>
              </div>
              <ReviewStars value={Number(review.rating) || 0} size="sm" />
              <p className="mt-2 whitespace-pre-line text-sm leading-6 text-[#4A7C75]">{review.comment}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
